import React, { useEffect, useState } from "react";
import CalendarAssorts from "../Assorts";
import CalendarExerciseChoice from "../ExerciseChoice";
import useCalendarSale from "../hooks/useCalendarSale";
import "./style/style.scss";
import { useMediaQuery } from "@mui/material";
import { Exercise } from "../../../models/Exercise";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import EAButton from "../../shared/Buttons/EAButton";
import EAModal from "../../shared/Modal/EAModal";
import { SelectionState } from "./hooks/useCalendarModal";
import StepIndicator from "./shared/StepIndicator";

interface IProps {
  exerciseName?: string;
  exercise: Exercise[];
  refreshCapacityAfterAddTicket: () => void;
  joinToExerciseByPass: (exerciseIds: string, assignType: boolean, passId: number) => void;
  clicked?: number;
  alertCalendar: string;
  onTimeSelect?: () => void;
}

/**
 * Component for handling multiple exercise choices
 */
const CalendarMultiExercise: React.FC<IProps> = ({
  exercise,
  clicked = SelectionState.NO_SELECTION,
  refreshCapacityAfterAddTicket,
  joinToExerciseByPass,
  alertCalendar,
  onTimeSelect,
}: IProps) => {
  const { t } = useTranslation();
  const {
    getTicketsByExercise,
    tickets,
    ticketLimit,
    ticketAccess,
    chooseTime,
    getDetailsAboutExercise,
    exerciseDetails,
  } = useCalendarSale();

  const [openHours, setOpenHours] = useState(false);
  const [whichClicked, setClicked] = useState(99);
  const isDesktop = useMediaQuery("(min-width: 1100px)");

  // Internal state to track if time was selected
  const [timeSelectionState, setTimeSelectionState] = useState(SelectionState.NO_SELECTION);

  const handleCloseModal = (boolean: boolean) => {
    setOpenHours(boolean);
  };

  useEffect(() => {
    if (chooseTime && chooseTime > 0) {
      const matchingIndex = exercise.findIndex((ex) => ex.startTime === chooseTime);
      if (matchingIndex !== -1) {
        setClicked(matchingIndex);
        setTimeSelectionState(SelectionState.TIME_SELECTED);
        return;
      }
    }
    setClicked(99);
    setTimeSelectionState(SelectionState.NO_SELECTION);
  }, [exercise, chooseTime]);

  const handleWhichClicked = (index: number) => {
    setClicked(index);

    // When a time is selected, update internal state and notify parent
    if (clicked >= SelectionState.DATE_SELECTED) {
      setTimeSelectionState(SelectionState.TIME_SELECTED);
      if (onTimeSelect) {
        onTimeSelect();
      }
    }
  };

  const getDetailsAboutClickedExercise = (exerciseId: number) => {
    getDetailsAboutExercise(exerciseId);
  };

  // Derive state from clicked prop
  const isDateSelected = clicked >= SelectionState.DATE_SELECTED;
  const isTimeSelected = timeSelectionState >= SelectionState.TIME_SELECTED || clicked >= SelectionState.TIME_SELECTED;

  return (
    <>
      {isDesktop ? (
        <>
          <div className="box exercises shadow border">
            <CalendarExerciseChoice
              getDetailsAboutClickedExercise={getDetailsAboutClickedExercise}
              exercise={exercise}
              getTicketsByExercise={getTicketsByExercise}
              handleWhichClicked={handleWhichClicked}
              whichClicked={whichClicked}
              onTimeSelect={onTimeSelect}
            />
          </div>
          {tickets.length && isTimeSelected ? (
            <div className="box tickets shadow border">
              <CalendarAssorts
                alertCalendar={alertCalendar}
                exerciseDetails={exerciseDetails}
                joinToExerciseByPass={joinToExerciseByPass}
                tickets={tickets}
                ticketAccess={ticketAccess}
                ticketLimit={ticketLimit}
                refreshCapacityAfterAddTicket={refreshCapacityAfterAddTicket}
              />
            </div>
          ) : (
            <div className="tickets"></div>
          )}
        </>
      ) : (
        <>
          <div className="calendar-modal-steps">
            <StepIndicator number={2} label={t("godzina_wejscia")} />
          </div>

          <div className="box-mobile flex-between-center">
            <div className="box-text">
              {!isTimeSelected ? (
                <b className="body-text-large">{t("godzina_wejscia")}</b>
              ) : (
                <>
                  <b className="body-text-small">Wybrano</b>
                  <b className="body-text-large primary-color">
                    {format(utcToZonedTime(chooseTime, "Europe/Warsaw"), `HH:mm`)}
                  </b>
                </>
              )}
            </div>

            <EAButton
              title={isTimeSelected ? t("zmien") : t("wybierz")}
              disabled={!isDateSelected}
              onClick={() => {
                setOpenHours(!openHours);
              }}
              variant={isTimeSelected ? "outlined" : "filled"}
            />
          </div>

          {isTimeSelected && (
            <>
              <div className="calendar-modal-steps">
                <StepIndicator number={3} label={t("wybierz_rodzaj_i_ilosc_biletow")} />
              </div>

              <CalendarAssorts
                alertCalendar={alertCalendar}
                exerciseDetails={exerciseDetails}
                joinToExerciseByPass={joinToExerciseByPass}
                tickets={tickets}
                ticketAccess={ticketAccess}
                ticketLimit={ticketLimit}
                refreshCapacityAfterAddTicket={refreshCapacityAfterAddTicket}
              />
            </>
          )}
        </>
      )}

      <EAModal isOpen={openHours} hideButtons onClose={() => setOpenHours(false)} title={t("wybierz_godzine")}>
        <div className="flex-start flex-wrap">
          <CalendarExerciseChoice
            getDetailsAboutClickedExercise={getDetailsAboutClickedExercise}
            exercise={exercise}
            getTicketsByExercise={getTicketsByExercise}
            handleCloseModal={handleCloseModal}
            handleWhichClicked={handleWhichClicked}
            whichClicked={whichClicked}
            onTimeSelect={onTimeSelect}
          />
        </div>
      </EAModal>
    </>
  );
};

export default CalendarMultiExercise;
